import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { masterUserPasswordSchema } from "../../../yup";
import { useSetMasterUserPasswordMutation } from "../../../services/api";
import AuthSplitLayout from "../../AuthSplitLayout";

const SetPassword = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(masterUserPasswordSchema),
  });
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [errMsg, setErrMsg] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [setMasterUserPassword, { isLoading }] =
    useSetMasterUserPasswordMutation();

  const newPasswordVal = watch("newPassword") || "";
  const hasLength = newPasswordVal.length >= 8;
  const hasLower = /[a-z]/.test(newPasswordVal);
  const hasUpper = /[A-Z]/.test(newPasswordVal);
  const hasNumber = /[0-9]/.test(newPasswordVal);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPasswordVal);
  
  const subConditionsMet = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  const hasThreeConditions = subConditionsMet >= 3;
  
  const areRequirementsMet = hasLength && hasThreeConditions;
  const showRequirementErrors = Boolean(newPasswordVal && !areRequirementsMet);

  useEffect(() => {
    if (newPasswordVal.length > 0 && !hasStartedTyping) {
      setHasStartedTyping(true);
    }
  }, [newPasswordVal, hasStartedTyping]);

  const onSubmit = async (values: FormValues) => {
    if (!token) {
      setErrMsg("Invalid or expired link.");
      return;
    }
    setErrMsg("");
    try {
      const result = await setMasterUserPassword({
        token,
        password: values.newPassword,
      }).unwrap();
      if (result?.success) {
        setIsSuccess(true);
      } else {
        setErrMsg(
          result?.message || "Failed to set password. Please try again."
        );
      }
    } catch (error: any) {
      setErrMsg(
        error?.data?.message || "Invalid or expired link. Please contact admin."
      );
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValue(name as keyof FormValues, value);
    setErrMsg("");
  };

  if (isSuccess) {
    return (
      <AuthSplitLayout>
        <div className="w-full max-w-sm flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-6">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-[#1f2937] mb-4 text-center font-['Proxima_Nova']">
            Password set successfully
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6 font-['Proxima_Nova']">
            Your password has been set. You can now log in with your new
            credentials.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md font-['Proxima_Nova']"
          >
            Continue to Login
          </button>
        </div>
      </AuthSplitLayout>
    );
  }

  const renderPasswordField = (
    name: "newPassword" | "repeatPassword",
    label: string,
    show: boolean,
    toggle: () => void,
    forceInvalidState: boolean = false
  ) => {
    const error = errors[name];
    const isInvalid = error || forceInvalidState;
    
    return (
      <div className="w-full flex flex-col mt-4">
        <div className="relative w-full">
          <input
            {...register(name)}
            id={name}
            type={show ? "text" : "password"}
            autoComplete="new-password"
            placeholder=" "
            onChange={onChange}
            disabled={isLoading}
            className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 pr-12 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
              isInvalid ? "border-red-500" : "border-gray-300"
            }`}
          />
          <label
            htmlFor={name}
            className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
              isInvalid ? "text-red-500" : "text-[#9CA3AF]"
            } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
          >
            {label}
          </label>
          <button
            type="button"
            onClick={toggle}
            tabIndex={-1}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {show ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
          </button>
        </div>
        {error && error.message && (
          <span className="mt-0.5 block text-xs font-medium text-red-500 capitalize">
            {error.message}
          </span>
        )}
      </div>
    );
  };

  return (
    <AuthSplitLayout>
      <div className="w-full max-w-sm">
        <h2 className="text-2xl font-semibold text-[#1f2937] mb-2 text-left font-['Proxima_Nova']">
          Set your password
        </h2>
        <p className="text-sm text-gray-500 text-left mb-6 font-['Proxima_Nova']">
          Choose a password to access your master account.
        </p>

        <form
          className="w-full flex flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          {renderPasswordField(
            "newPassword",
            "New Password*",
            showNewPassword,
            () => setShowNewPassword((s) => !s),
            showRequirementErrors 
          )}
          {renderPasswordField(
            "repeatPassword",
            "Repeat Password*",
            showRepeatPassword,
            () => setShowRepeatPassword((s) => !s),
            showRequirementErrors 
          )}

          {hasStartedTyping && (
            <div className="mt-4 p-4 border border-gray-200 rounded-lg text-gray-600 text-[13px]">
              <p className="mb-3 font-medium text-sm">Your password must contain:</p>
              <div className="flex flex-col gap-2.5">
                <div className={`flex items-center gap-2 ${hasLength ? "text-green-600" : ""}`}>
                  {hasLength ? (
                    <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <span className="w-[18px] text-center leading-none text-[18px] text-gray-400">•</span>
                  )}
                  <span>At least 8 characters</span>
                </div>
                
                <div className={`flex items-center gap-2 ${hasThreeConditions ? "text-green-600" : ""}`}>
                  {hasThreeConditions ? (
                    <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <span className="w-[18px] text-center leading-none text-[18px] text-gray-400">•</span>
                  )}
                  <span>At least 3 of the following:</span>
                </div>
                
                <div className="flex flex-col gap-2.5 pl-[26px]">
                  <div className={`flex items-center gap-2 ${hasLower ? "text-green-600" : ""}`}>
                    {hasLower ? (
                      <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <span className="w-[18px] text-center leading-none text-[18px] text-gray-400">•</span>
                    )}
                    <span>Lower case letters (a-z)</span>
                  </div>
                  
                  <div className={`flex items-center gap-2 ${hasUpper ? "text-green-600" : ""}`}>
                    {hasUpper ? (
                      <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <span className="w-[18px] text-center leading-none text-[18px] text-gray-400">•</span>
                    )}
                    <span>Upper case letters (A-Z)</span>
                  </div>
                  
                  <div className={`flex items-center gap-2 ${hasNumber ? "text-green-600" : ""}`}>
                    {hasNumber ? (
                      <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <span className="w-[18px] text-center leading-none text-[18px] text-gray-400">•</span>
                    )}
                    <span>Numbers (0-9)</span>
                  </div>
                  
                  <div className={`flex items-center gap-2 ${hasSpecial ? "text-green-600" : ""}`}>
                    {hasSpecial ? (
                      <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <span className="w-[18px] text-center leading-none text-[18px] text-gray-400">•</span>
                    )}
                    <span>Special characters (e.g. !@#$%^&*)</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {errMsg && (
            <span className="mt-4 block text-xs font-medium text-red-500">
              {errMsg}
            </span>
          )}

          <button
            type="submit"
            disabled={isLoading || !areRequirementsMet}
            className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
          >
            <span className={isLoading ? "opacity-0" : ""}>Set password</span>
            {isLoading && (
              <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
          </button>
        </form>
      </div>
    </AuthSplitLayout>
  );
};

export default SetPassword;
